import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import {InferAnnotationParamsResponse} from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AnnotationAssistantDAO';
import AnnotationAssistantFeedback from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AnnotationAssistantFeedback';
import {PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {FONT_PROXIMA_12, FONT_PROXIMA_13} from 'styles/FontConstants';
import PreviewBadge from 'ui-library/badges/PreviewBadge';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_SEMIDARK, GRAY_DARKER, GRAY_LIGHT, RED} from 'ui-library/Colors';
import strUtil from 'util/strUtil';

export const MAX_PROMPT_LENGTH = 10000;

export interface AnnotationAssistantInputProps {
  onSubmitAIPrompt: (userPrompt: string) => Promise<InferAnnotationParamsResponse>;
  onApplyAISuggestion: () => void;
  onDiscardAISuggestion: () => void;
  aiSuggestedPlateMap: PlateMap | null;
}

export default function AnnotationAssistantInput({
  onSubmitAIPrompt,
  onApplyAISuggestion,
  onDiscardAISuggestion,
  aiSuggestedPlateMap,
}: AnnotationAssistantInputProps): JSX.Element {
  const [userPrompt, setUserPrompt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [suggestionTraceId, setSuggestionTraceId] = useState<string>('');
  const [promptResponseError, setPromptResponseError] = useState<string | null>(null);
  const [hasExceededMaxLength, setHasExceededMaxLength] = useState<boolean>(false);

  const handleSubmitPrompt = async (): Promise<void> => {
    setIsSubmitting(true);
    setPromptResponseError(null);
    const res = await onSubmitAIPrompt(userPrompt);
    trackEvent(STORABLE_ANALYTICS_ACTIONS.SUBMITTED_ROLE_ANNOTATION_AI_PROMPT, {
      traceId: res.traceId,
      error: res.error,
    });
    setSuggestionTraceId(res.traceId);
    setIsSubmitting(false);
    if (res.error != null) {
      setPromptResponseError(res.error);
    }
  };

  const handleDiscardAISuggestion = (): void => {
    setSuggestionTraceId('');
    onDiscardAISuggestion();
  };

  return (
    // Negative top margin is to position the beta badge within the top margin
    <div
      style={{marginTop: '-16px'}}
      {...getTestProps({component: 'AnnotationAssistantInput', element: 'root'})}
    >
      <PreviewBadge />
      <p className={css(styles.helpText)}>
        Describe or paste a plate layout (from a table or CSV) using sample, control, standard, and blank
        roles.
      </p>
      <div className={css(styles.textareaContainer)}>
        <textarea
          className={css(styles.textarea)}
          placeholder="Describe or paste a plate layout here..."
          maxLength={MAX_PROMPT_LENGTH}
          rows={4}
          value={userPrompt}
          onChange={(e) => {
            setUserPrompt(e.target.value);
            if (e.target.value.length >= MAX_PROMPT_LENGTH) {
              setHasExceededMaxLength(true);
            } else {
              setHasExceededMaxLength(false);
            }
          }}
          {...getTestProps({component: 'AnnotationAssistantInput', element: 'textarea'})}
        />
        <div className={css(styles.aiButtons)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            size={ButtonSize.SMALL}
            onClick={handleSubmitPrompt}
            icon="arrow-up"
            iconAltText="Submit Prompt"
            disabled={userPrompt === '' || isSubmitting}
          />
        </div>
      </div>
      {aiSuggestedPlateMap != null && promptResponseError == null && !isSubmitting && (
        <div className={css(styles.aiButtons)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            text="Discard"
            onClick={handleDiscardAISuggestion}
          />
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.SMALL}
            text="Apply"
            onClick={onApplyAISuggestion}
          />
        </div>
      )}
      {promptResponseError != null ? (
        // Show only the error messsage if there is an error
        <div className={css(styles.aiErrorText)}>
          {`${strUtil.ensureTrailingPeriod(promptResponseError)} Try again or adjust your prompt.`}
        </div>
      ) : isSubmitting ? (
        // show only the loading message while the prompt is being processed
        <div className={css(styles.aiHelpText)}>
          <LLMLoadingMessage text="Processing your prompt..." />
        </div>
      ) : hasExceededMaxLength ? (
        // if the user has maxed out the prompt length, let them know
        <p className={css(styles.aiErrorText)}>
          Your input has reached the maximum length of {MAX_PROMPT_LENGTH.toLocaleString()} characters.
        </p>
      ) : suggestionTraceId !== '' && !isSubmitting ? (
        // show the AI feedback widget if a suggestion has been made and the prompt is not being processed
        <div className={css(styles.aiHelpText)}>
          <AnnotationAssistantFeedback userPrompt={userPrompt} traceId={suggestionTraceId} />
        </div>
      ) : (
        // otherwise, show the AI disclaimer
        <div className={css(styles.aiHelpText)}>AI can make mistakes. Always check results.</div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  helpText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginTop: 8,
  },
  aiHelpText: {
    ...FONT_PROXIMA_12,
    float: 'right',
    clear: 'right',
    margin: '8px',
    color: GRAY_SEMIDARK,
  },
  aiErrorText: {
    ...FONT_PROXIMA_12,
    color: RED,
  },
  banner: {
    marginBottom: 8,
  },
  textareaContainer: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 6,
    padding: 4,
    marginBottom: 8,
  },
  textarea: {
    resize: 'none',
    marginBottom: 8,
    border: 'none',
  },
  aiButtons: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
});
